import type { CellData, RowData } from "@tanstack/react-table";
import { Subscribe } from "@tanstack/react-table";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ReactNode } from "react";
import type { Column } from "~/shared/react-table/app-table";

import { cn } from "~/utils";

interface DataTableColumnHeaderProps<
  TData extends RowData,
  TValue extends CellData,
> extends Omit<React.HTMLAttributes<HTMLDivElement>, "prefix"> {
  column: Column<TData, TValue>;
  title: string;
  prefix?: ReactNode;
}

export function DataTableColumnHeader<
  TData extends RowData,
  TValue extends CellData,
>({
  column,
  title,
  className,
  prefix,
}: DataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return (
      <div
        className={cn("flex items-center gap-1 text-secondary-600", className)}
      >
        {title}
      </div>
    );
  }

  return (
    <Subscribe
      source={column.table.atoms.sorting}
      selector={() => column.getIsSorted()}
    >
      {(sortDirection) => (
        <button
          type="button"
          className={cn(
            "flex w-full cursor-pointer items-center gap-1 p-0 text-left text-secondary-600",
            className,
          )}
          onClick={() => {
            if (sortDirection === "desc") {
              column.clearSorting();
              return;
            }

            column.toggleSorting(sortDirection === "asc", false);
          }}
        >
          {prefix ? <span>{prefix}</span> : null}
          <span className="inline-flex min-w-0 items-center gap-1 whitespace-nowrap">
            {title}
            <span className="flex w-4 shrink-0 flex-col" aria-hidden="true">
              <ChevronUp
                className={cn(
                  "size-4",
                  sortDirection === "asc"
                    ? "text-primary-900"
                    : "text-secondary-600",
                )}
                strokeWidth={2.5}
              />
              <ChevronDown
                className={cn(
                  "size-4 -mt-2",
                  sortDirection === "desc"
                    ? "text-primary-900"
                    : "text-secondary-600",
                )}
                strokeWidth={2.5}
              />
            </span>
          </span>
        </button>
      )}
    </Subscribe>
  );
}
